import { dropLastWhile } from "@suse-o11y/drop-last-while";
import { filter } from "@suse-o11y/filter";
import { flat } from "@suse-o11y/flat";
import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { isEmpty } from "@suse-o11y/is-empty";
import { isNull } from "@suse-o11y/is-null";
import { isPresent } from "@suse-o11y/is-present";
import type { JSON } from "@suse-o11y/json";
import { map } from "@suse-o11y/map";
import { pipe } from "@suse-o11y/pipe";
import type { Record } from "@suse-o11y/record";
import { sortBy } from "@suse-o11y/sort-by";
import { take } from "@suse-o11y/take";
import { toEntries } from "@suse-o11y/to-entries";
import { z } from "zod";

class Encoder {
	public static primitive<T extends string | number | boolean | null>(input: undefined | T, defaults?: T): Encoder {
		const defaulted = input ?? defaults ?? null;

		return new Encoder(defaulted, defaulted === defaults);
	}

	public static list<T>(
		items: undefined | ReadonlyArray<T>,
		defaults: ReadonlyArray<T>,
		transform: (entry: T) => Encoder,
	): Encoder {
		const encoded = Encoder.dropDefaultedTail((items ?? defaults).map(transform));
		const defaulted = Encoder.dropDefaultedTail(defaults.map(transform));

		return new Encoder(encoded, isDeepEqual(encoded, defaulted));
	}

	public static tuple<const T extends ReadonlyArray<Encoder>>(...elements: T): Encoder {
		const encoded = Encoder.dropDefaultedTail(elements);

		return new Encoder(encoded, isEmpty(encoded));
	}

	/**
	 * Encode a record as a single dimensional Array with keys on even and values on odd positions.
	 * This format minimizes produced numeric fields tokens.
	 *
	 * @example
	 * Encoder.shape({
	 *   0: Encoder.primitive(42),
	 *   1: Encoder.primitive("test"),
	 * }) == [0, 42, 1, "test"]
	 * // vs {"0": 42, "1": "test"}
	 */
	public static shape<T extends Record<string | number, Encoder>>(record: T): Encoder {
		const encoded = pipe(
			record,
			toEntries(),
			// Sort is lexicographic on stringified keys (e.g. "0","1","10","11","2",...).
			// Pair order is irrelevant for correctness — the decoder re-pairs purely
			// positionally via `chunk(2) → fromEntries`. The sort exists only to make
			// the encoded output stable and deterministic for identical inputs.
			sortBy(([key]) => key),
			map(([key, encoder]) => {
				const value = encoder.encode();

				if (value == null) {
					return null;
				}

				return [z.coerce.number().safeParse(key).data ?? key, value];
			}),
			filter(isPresent),
			flat(),
		);

		return new Encoder(encoded, isEmpty(encoded));
	}

	private static dropDefaultedTail(elements: ReadonlyArray<Encoder>): ReadonlyArray<JSON> {
		const defaulted = dropLastWhile(
			elements.map((encoder) => encoder.encode()),
			isNull,
		);

		// drop the defaulted tail and take the rest without default values
		return pipe(
			elements,
			map((encoder) => encoder.value),
			take(defaulted.length),
		);
	}

	private constructor(
		private readonly value: JSON,
		private readonly isDefault: boolean,
	) {}

	public encode(): JSON {
		return this.isDefault ? null : this.value;
	}
}

export { Encoder };
